'use client';

import React, { useState } from 'react';
import Link from 'next/link';

// ─── TYPES ────────────────────────────────────────────────────────────────────

interface LayerAlignment {
  layer: number;
  label: string;
  forestLayer: string;
  irlLayer: string;
  yaqeenStage: string;
  color: string;
  forestColor: string;
  irlColor: string;
  yaqeenColor: string;
  emergenceTrigger: string;
  crossContextConnections: string[];
}

interface EmergenceTrigger {
  id: string;
  context: 'forest' | 'irl' | 'yaqeen';
  label: string;
  condition: string;
  status: 'active' | 'dormant' | 'triggered';
  linkedLayers: number[];
  color: string;
}

// ─── 8-LAYER ALIGNMENT DATA ───────────────────────────────────────────────────

const LAYER_ALIGNMENTS: LayerAlignment[] = [
  {
    layer: 1,
    label: 'Encounter / Zahir',
    forestLayer: 'Highland / Canopy',
    irlLayer: 'Hawas Encounter',
    yaqeenStage: 'Ilm — First Contact',
    color: '#2d7a47',
    forestColor: '#2d7a47',
    irlColor: '#2d8a87',
    yaqeenColor: '#2d7a47',
    emergenceTrigger: 'First sensory contact with any created thing',
    crossContextConnections: ['Canopy Photosynthesis (S3)', 'Three Questions Engine', 'Ilm Yaqeen entry'],
  },
  {
    layer: 2,
    label: 'Three Questions',
    forestLayer: 'Understory',
    irlLayer: 'Is it? / What is it? / When is it?',
    yaqeenStage: 'Ilm — Examination',
    color: '#2d8a87',
    forestColor: '#2d7a47',
    irlColor: '#2d8a87',
    yaqeenColor: '#2d7a47',
    emergenceTrigger: 'Caretaker applies Wujud / Huduth Dhati / Huduth Zamani',
    crossContextConnections: ['Understory shade dynamics', 'Fission Reactor', 'Ilm deepening'],
  },
  {
    layer: 3,
    label: 'Names Extraction / Fission',
    forestLayer: 'Forest Floor',
    irlLayer: 'Leaf Sequence / Names Surfacing',
    yaqeenStage: 'Ilm → Ayn Transition',
    color: '#D4A82A',
    forestColor: '#2d7a47',
    irlColor: '#c9a84c',
    yaqeenColor: '#2d8a87',
    emergenceTrigger: 'First Name extracted from a direct encounter',
    crossContextConnections: ['Decomposition layer', 'Names Reference cross-link', 'Ayn Yaqeen threshold'],
  },
  {
    layer: 4,
    label: 'Sunnat Allah',
    forestLayer: 'Root Zone',
    irlLayer: 'Divine Pattern Recognition',
    yaqeenStage: 'Ayn — Pattern Witness',
    color: '#c9a84c',
    forestColor: '#2d7a47',
    irlColor: '#c9a84c',
    yaqeenColor: '#2d8a87',
    emergenceTrigger: 'Caretaker identifies a recurring divine pattern across 2+ encounters',
    crossContextConnections: ['Root zone nutrient flow', 'Sunnat Timeline', 'Ayn Yaqeen consolidation'],
  },
  {
    layer: 5,
    label: 'Zahir-Batin Depth',
    forestLayer: 'Mycorrhizal Network',
    irlLayer: 'Hidden Connection Mapping',
    yaqeenStage: 'Ayn — Deep Witness',
    color: '#8a8ab8',
    forestColor: '#2d7a47',
    irlColor: '#8a8ab8',
    yaqeenColor: '#2d8a87',
    emergenceTrigger: 'Caretaker perceives hidden connection between two visible phenomena',
    crossContextConnections: ['Mycorrhizal Network (S4)', 'Batin Depth Panel', 'Ayn Yaqeen peak'],
  },
  {
    layer: 6,
    label: 'Haqq — Truth of the Thing',
    forestLayer: 'Soil Composition',
    irlLayer: 'Ayah Declaration',
    yaqeenStage: 'Haqq — Reality Arrival',
    color: '#c9a84c',
    forestColor: '#2d7a47',
    irlColor: '#c9a84c',
    yaqeenColor: '#c9a84c',
    emergenceTrigger: 'Caretaker articulates "This encounter is an Ayah declaring that..."',
    crossContextConnections: ['Soil mineral composition', 'Haqq Layer declaration', 'Haqq Yaqeen entry'],
  },
  {
    layer: 7,
    label: 'Amanah — The Trust',
    forestLayer: 'Bedrock',
    irlLayer: 'Action Bridge',
    yaqeenStage: 'Haqq — Embodied Action',
    color: '#D4A82A',
    forestColor: '#2d7a47',
    irlColor: '#D4A82A',
    yaqeenColor: '#c9a84c',
    emergenceTrigger: 'Caretaker bridges recognition to a concrete Amanah action',
    crossContextConnections: ['Bedrock stability', 'MRG Amanah Bridge', 'Haqq Yaqeen deepening'],
  },
  {
    layer: 8,
    label: 'Uboor — The Crossing',
    forestLayer: 'Groundwater / Return Flow',
    irlLayer: 'Recognition Arc Completion',
    yaqeenStage: 'Haqq — Platform Disappears',
    color: '#5a5a8a',
    forestColor: '#2d7a47',
    irlColor: '#5a5a8a',
    yaqeenColor: '#c9a84c',
    emergenceTrigger: 'Caretaker closes the platform and goes into the world — Haqq Yaqeen complete',
    crossContextConnections: ['Groundwater cycle return', 'Uboor Bridge-Quest', '"Go see for yourself"'],
  },
];

// ─── EMERGENCE TRIGGERS ───────────────────────────────────────────────────────

const EMERGENCE_TRIGGERS: EmergenceTrigger[] = [
  { id: 'et-1', context: 'forest', label: 'Canopy Encounter', condition: 'First IRL observation submitted', status: 'active', linkedLayers: [1, 2], color: '#2d7a47' },
  { id: 'et-2', context: 'irl', label: 'Names Surfacing', condition: '3+ Names extracted in one session', status: 'active', linkedLayers: [3, 4], color: '#c9a84c' },
  { id: 'et-3', context: 'yaqeen', label: 'Ayn Threshold', condition: 'First Ayn Yaqeen recognition logged', status: 'dormant', linkedLayers: [3, 5], color: '#2d8a87' },
  { id: 'et-4', context: 'forest', label: 'Mycorrhizal Link', condition: 'Two observations from same ward linked', status: 'dormant', linkedLayers: [5, 6], color: '#8a8ab8' },
  { id: 'et-5', context: 'irl', label: 'Hikmah Station', condition: 'Observations in 2+ adjacent biomes', status: 'dormant', linkedLayers: [4, 5, 6], color: '#D4A82A' },
  { id: 'et-6', context: 'yaqeen', label: 'Haqq Arrival', condition: 'First Haqq Yaqeen recognition logged', status: 'dormant', linkedLayers: [6, 7, 8], color: '#c9a84c' },
  { id: 'et-7', context: 'forest', label: 'Watershed Descent', condition: 'All 8 IRL layers completed in one observation', status: 'dormant', linkedLayers: [1, 2, 3, 4, 5, 6, 7, 8], color: '#2d7a47' },
  { id: 'et-8', context: 'irl', label: 'Uboor Crossing', condition: 'Caretaker bridges IRL to MRG action', status: 'dormant', linkedLayers: [7, 8], color: '#5a5a8a' },
];

// ─── COLOR PALETTE SYNC ───────────────────────────────────────────────────────

const COLOR_PALETTE = {
  forest: { primary: '#2d7a47', secondary: '#1a5c32', accent: '#4aab6d', label: 'Forest / Creation' },
  grasslands: { primary: '#D4A82A', secondary: '#a07a1a', accent: '#f0c84a', label: 'Grasslands / Patience' },
  wetlands: { primary: '#2d8a87', secondary: '#1a6a67', accent: '#4aada9', label: 'Wetlands / Purification' },
  desert: { primary: '#c9a84c', secondary: '#9a7a2c', accent: '#e8c86c', label: 'Desert / Endurance' },
  savannah: { primary: '#8a6a2a', secondary: '#6a4a1a', accent: '#aa8a4a', label: 'Savannah / Transition' },
  coastal: { primary: '#2d5a8a', secondary: '#1a3a6a', accent: '#4a7aaa', label: 'Coastal / Boundary' },
  mountain: { primary: '#5a5a8a', secondary: '#3a3a6a', accent: '#7a7aaa', label: 'Mountain / Elevation' },
  urban: { primary: '#8a8a8a', secondary: '#6a6a6a', accent: '#aaaaaa', label: 'Urban / Stewardship' },
};

const AYAT_TRACK_COLORS = {
  creation: '#2d7a47',
  revelation: '#c9a84c',
  inner: '#8a8ab8',
};

// ─── CROSS-CONTEXT CONNECTION MAP ─────────────────────────────────────────────

interface CrossContextNode {
  id: string;
  label: string;
  context: 'forest' | 'irl' | 'yaqeen' | 'mrg' | 'names';
  layer: number;
  connections: string[];
  color: string;
}

const CROSS_CONTEXT_NODES: CrossContextNode[] = [
  { id: 'forest-canopy', label: 'Canopy (S3)', context: 'forest', layer: 1, connections: ['irl-hawas', 'names-nur', 'yaqeen-ilm-1'], color: '#2d7a47' },
  { id: 'irl-hawas', label: 'Hawas Encounter', context: 'irl', layer: 1, connections: ['forest-canopy', 'yaqeen-ilm-1', 'names-basir'], color: '#2d8a87' },
  { id: 'yaqeen-ilm-1', label: 'Ilm Yaqeen', context: 'yaqeen', layer: 1, connections: ['irl-hawas', 'forest-canopy', 'mrg-study'], color: '#2d7a47' },
  { id: 'forest-myco', label: 'Mycorrhizal (S4)', context: 'forest', layer: 5, connections: ['irl-batin', 'names-wadud', 'yaqeen-ayn-5'], color: '#2d7a47' },
  { id: 'irl-batin', label: 'Zahir-Batin Depth', context: 'irl', layer: 5, connections: ['forest-myco', 'yaqeen-ayn-5', 'names-latif'], color: '#8a8ab8' },
  { id: 'yaqeen-ayn-5', label: 'Ayn Yaqeen', context: 'yaqeen', layer: 5, connections: ['irl-batin', 'forest-myco', 'mrg-witness'], color: '#2d8a87' },
  { id: 'forest-bedrock', label: 'Bedrock', context: 'forest', layer: 7, connections: ['irl-amanah', 'names-qadir', 'yaqeen-haqq-7'], color: '#2d7a47' },
  { id: 'irl-amanah', label: 'Amanah Bridge', context: 'irl', layer: 7, connections: ['forest-bedrock', 'yaqeen-haqq-7', 'mrg-action'], color: '#D4A82A' },
  { id: 'yaqeen-haqq-7', label: 'Haqq Yaqeen', context: 'yaqeen', layer: 7, connections: ['irl-amanah', 'forest-bedrock', 'mrg-action'], color: '#c9a84c' },
  { id: 'mrg-action', label: 'MRG Amanah Action', context: 'mrg', layer: 7, connections: ['irl-amanah', 'yaqeen-haqq-7', 'names-razzaq'], color: '#2d7a47' },
  { id: 'names-nur', label: 'An-Nur (Light)', context: 'names', layer: 1, connections: ['forest-canopy', 'irl-hawas'], color: '#c9a84c' },
  { id: 'names-latif', label: 'Al-Latif (Subtle)', context: 'names', layer: 5, connections: ['irl-batin', 'forest-myco'], color: '#8a8ab8' },
  { id: 'names-qadir', label: 'Al-Qadir (All-Powerful)', context: 'names', layer: 7, connections: ['forest-bedrock', 'irl-amanah'], color: '#D4A82A' },
];

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function Plot4AdminPanel() {
  const [activeTab, setActiveTab] = useState<'alignment' | 'palette' | 'emergence' | 'connections'>('alignment');
  const [selectedLayer, setSelectedLayer] = useState<number | null>(null);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [triggerFilter, setTriggerFilter] = useState<'all' | 'forest' | 'irl' | 'yaqeen'>('all');

  const filteredTriggers = triggerFilter === 'all'
    ? EMERGENCE_TRIGGERS
    : EMERGENCE_TRIGGERS.filter(t => t.context === triggerFilter);

  const selectedLayerData = selectedLayer !== null
    ? LAYER_ALIGNMENTS.find(l => l.layer === selectedLayer)
    : null;

  const selectedNodeData = selectedNode
    ? CROSS_CONTEXT_NODES.find(n => n.id === selectedNode)
    : null;

  const connectedNodes = selectedNodeData
    ? CROSS_CONTEXT_NODES.filter(n => selectedNodeData.connections.includes(n.id))
    : [];

  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(6,13,18,0.98)', border: '1px solid rgba(201,168,76,0.15)' }}>
      {/* Header */}
      <div className="px-6 py-5 border-b" style={{ borderColor: 'rgba(201,168,76,0.1)', background: 'rgba(201,168,76,0.03)' }}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-widest mb-1" style={{ color: 'rgba(201,168,76,0.5)' }}>◈ Admin · Plot 04</p>
            <h2 className="font-mono text-xl font-black uppercase tracking-tight" style={{ color: '#F5F0E8' }}>
              8-Layer Alignment Panel
            </h2>
            <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 12, marginTop: 2 }}>
              Forest / IRL Lens / Yaqeen — cross-context architecture audit
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {(['alignment', 'palette', 'emergence', 'connections'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className="font-mono text-[9px] uppercase tracking-widest px-3 py-1.5 rounded transition-all"
                style={{
                  background: activeTab === tab ? 'rgba(201,168,76,0.12)' : 'transparent',
                  border: `1px solid ${activeTab === tab ? 'rgba(201,168,76,0.4)' : 'rgba(201,168,76,0.12)'}`,
                  color: activeTab === tab ? '#c9a84c' : 'rgba(212,197,169,0.4)',
                }}
              >
                {tab === 'alignment' ? '8-Layer Alignment' : tab === 'palette' ? 'Color Palette' : tab === 'emergence' ? 'Emergence Audit' : 'Connection Map'}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* ── TAB: 8-LAYER ALIGNMENT ── */}
        {activeTab === 'alignment' && (
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest mb-5" style={{ color: 'rgba(212,197,169,0.4)' }}>
              Forest Watershed ↔ IRL Lens ↔ Yaqeen Progression — all three contexts synchronized
            </p>
            {/* Column headers */}
            <div className="grid grid-cols-4 gap-3 mb-3">
              {['Layer', 'Forest Watershed', 'IRL Lens', 'Yaqeen Stage'].map(h => (
                <div key={h} className="font-mono text-[8px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.3)' }}>{h}</div>
              ))}
            </div>
            <div className="space-y-2">
              {LAYER_ALIGNMENTS.map(layer => (
                <div
                  key={layer.layer}
                  onClick={() => setSelectedLayer(selectedLayer === layer.layer ? null : layer.layer)}
                  className="grid grid-cols-4 gap-3 rounded-lg p-3 cursor-pointer transition-all"
                  style={{
                    background: selectedLayer === layer.layer ? `${layer.color}10` : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${selectedLayer === layer.layer ? `${layer.color}30` : 'rgba(255,255,255,0.04)'}`,
                  }}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center font-mono text-[9px] font-black flex-shrink-0" style={{ background: `${layer.color}20`, color: layer.color }}>
                      {layer.layer}
                    </div>
                    <span className="font-mono text-[9px] uppercase tracking-widest" style={{ color: '#d4c5a9' }}>{layer.label}</span>
                  </div>
                  <div>
                    <span className="font-mono text-[9px]" style={{ color: layer.forestColor }}>{layer.forestLayer}</span>
                  </div>
                  <div>
                    <span className="font-mono text-[9px]" style={{ color: layer.irlColor }}>{layer.irlLayer}</span>
                  </div>
                  <div>
                    <span className="font-mono text-[9px]" style={{ color: layer.yaqeenColor }}>{layer.yaqeenStage}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Expanded layer detail */}
            {selectedLayerData && (
              <div className="mt-4 rounded-xl p-5" style={{ background: `${selectedLayerData.color}08`, border: `1px solid ${selectedLayerData.color}25` }}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center font-mono text-[10px] font-black" style={{ background: `${selectedLayerData.color}20`, color: selectedLayerData.color }}>
                    {selectedLayerData.layer}
                  </div>
                  <h3 className="font-mono text-sm font-black uppercase tracking-wide" style={{ color: '#F5F0E8' }}>
                    Layer {selectedLayerData.layer}: {selectedLayerData.label}
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="rounded-lg p-3" style={{ background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.15)' }}>
                    <p className="font-mono text-[8px] uppercase tracking-widest mb-1" style={{ color: 'rgba(45,122,71,0.6)' }}>Emergence Trigger</p>
                    <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 12 }}>{selectedLayerData.emergenceTrigger}</p>
                  </div>
                  <div className="rounded-lg p-3" style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.12)' }}>
                    <p className="font-mono text-[8px] uppercase tracking-widest mb-2" style={{ color: 'rgba(201,168,76,0.5)' }}>Cross-Context Connections</p>
                    <div className="space-y-1">
                      {selectedLayerData.crossContextConnections.map((c, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span style={{ color: selectedLayerData.color, fontSize: 10 }}>→</span>
                          <span style={{ color: 'rgba(212,197,169,0.6)', fontSize: 11 }}>{c}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── TAB: COLOR PALETTE SYNC ── */}
        {activeTab === 'palette' && (
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest mb-5" style={{ color: 'rgba(212,197,169,0.4)' }}>
              Biome ring colors → Ayat track colors → Module tiles — synchronized palette
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {Object.entries(COLOR_PALETTE).map(([key, palette]) => (
                <div key={key} className="rounded-xl p-4" style={{ background: `${palette.primary}08`, border: `1px solid ${palette.primary}20` }}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-full flex-shrink-0" style={{ background: palette.primary }} />
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-widest" style={{ color: palette.primary }}>{palette.label}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {[palette.primary, palette.secondary, palette.accent].map((c, i) => (
                      <div key={i} className="flex-1 rounded-lg p-2 text-center" style={{ background: `${c}15`, border: `1px solid ${c}25` }}>
                        <div className="w-full h-4 rounded mb-1" style={{ background: c }} />
                        <p className="font-mono text-[7px]" style={{ color: 'rgba(212,197,169,0.4)' }}>{c}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Ayat Track Colors */}
            <div className="rounded-xl p-5 mb-6" style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.12)' }}>
              <p className="font-mono text-[9px] uppercase tracking-widest mb-4" style={{ color: 'rgba(201,168,76,0.5)' }}>Ayat Track Colors</p>
              <div className="grid grid-cols-3 gap-4">
                {Object.entries(AYAT_TRACK_COLORS).map(([track, color]) => (
                  <div key={track} className="rounded-lg p-3 text-center" style={{ background: `${color}10`, border: `1px solid ${color}25` }}>
                    <div className="w-full h-6 rounded mb-2" style={{ background: color }} />
                    <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color }}>{track}</p>
                    <p className="font-mono text-[8px] mt-1" style={{ color: 'rgba(212,197,169,0.4)' }}>{color}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Yaqeen Level Colors */}
            <div className="rounded-xl p-5" style={{ background: 'rgba(45,138,135,0.04)', border: '1px solid rgba(45,138,135,0.12)' }}>
              <p className="font-mono text-[9px] uppercase tracking-widest mb-4" style={{ color: 'rgba(45,138,135,0.5)' }}>Yaqeen Level Colors (Module Tile States)</p>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: 'Ilm Yaqeen', color: '#2d7a47', desc: 'Knowledge state — green' },
                  { label: 'Ayn Yaqeen', color: '#2d8a87', desc: 'Perception state — teal' },
                  { label: 'Haqq Yaqeen', color: '#c9a84c', desc: 'Reality state — gold' },
                ].map(y => (
                  <div key={y.label} className="rounded-lg p-3" style={{ background: `${y.color}10`, border: `1px solid ${y.color}25` }}>
                    <div className="w-full h-6 rounded mb-2" style={{ background: y.color }} />
                    <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color: y.color }}>{y.label}</p>
                    <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 10, marginTop: 2 }}>{y.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── TAB: EMERGENCE TRIGGER AUDIT ── */}
        {activeTab === 'emergence' && (
          <div>
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <p className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.4)' }}>
                Emergence triggers across Forest / IRL / Yaqeen contexts
              </p>
              <div className="flex gap-2">
                {(['all', 'forest', 'irl', 'yaqeen'] as const).map(f => (
                  <button
                    key={f}
                    onClick={() => setTriggerFilter(f)}
                    className="font-mono text-[8px] uppercase tracking-widest px-2 py-1 rounded transition-all"
                    style={{
                      background: triggerFilter === f ? 'rgba(201,168,76,0.1)' : 'transparent',
                      border: `1px solid ${triggerFilter === f ? 'rgba(201,168,76,0.3)' : 'rgba(201,168,76,0.1)'}`,
                      color: triggerFilter === f ? '#c9a84c' : 'rgba(212,197,169,0.4)',
                    }}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            {/* Summary stats */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              {[
                { label: 'Active', count: EMERGENCE_TRIGGERS.filter(t => t.status === 'active').length, color: '#2d7a47' },
                { label: 'Dormant', count: EMERGENCE_TRIGGERS.filter(t => t.status === 'dormant').length, color: '#8a8ab8' },
                { label: 'Triggered', count: EMERGENCE_TRIGGERS.filter(t => t.status === 'triggered').length, color: '#c9a84c' },
              ].map(s => (
                <div key={s.label} className="rounded-lg p-3 text-center" style={{ background: `${s.color}08`, border: `1px solid ${s.color}20` }}>
                  <div className="font-mono text-2xl font-black" style={{ color: s.color }}>{s.count}</div>
                  <div className="font-mono text-[8px] uppercase tracking-widest mt-1" style={{ color: 'rgba(212,197,169,0.4)' }}>{s.label}</div>
                </div>
              ))}
            </div>

            <div className="space-y-3">
              {filteredTriggers.map(trigger => (
                <div key={trigger.id} className="rounded-xl p-4" style={{ background: `${trigger.color}06`, border: `1px solid ${trigger.color}18` }}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: trigger.status === 'active' ? '#2d7a47' : trigger.status === 'triggered' ? '#c9a84c' : '#5a5a8a' }} />
                      <span className="font-mono text-[10px] uppercase tracking-widest font-black" style={{ color: trigger.color }}>{trigger.label}</span>
                      <span className="font-mono text-[8px] px-1.5 py-0.5 rounded uppercase" style={{ background: `${trigger.color}15`, color: trigger.color }}>{trigger.context}</span>
                    </div>
                    <span className="font-mono text-[8px] uppercase tracking-widest px-2 py-0.5 rounded" style={{
                      background: trigger.status === 'active' ? 'rgba(45,122,71,0.12)' : trigger.status === 'triggered' ? 'rgba(201,168,76,0.12)' : 'rgba(90,90,138,0.12)',
                      color: trigger.status === 'active' ? '#2d7a47' : trigger.status === 'triggered' ? '#c9a84c' : '#8a8ab8',
                    }}>
                      {trigger.status}
                    </span>
                  </div>
                  <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 12, marginBottom: 8 }}>{trigger.condition}</p>
                  <div className="flex gap-1 flex-wrap">
                    {trigger.linkedLayers.map(l => (
                      <span key={l} className="font-mono text-[8px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(212,197,169,0.4)', border: '1px solid rgba(255,255,255,0.06)' }}>
                        L{l}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── TAB: CROSS-CONTEXT CONNECTION MAP ── */}
        {activeTab === 'connections' && (
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest mb-5" style={{ color: 'rgba(212,197,169,0.4)' }}>
              Select a node to see its cross-context connections
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
              {CROSS_CONTEXT_NODES.map(node => (
                <div
                  key={node.id}
                  onClick={() => setSelectedNode(selectedNode === node.id ? null : node.id)}
                  className="rounded-lg p-3 cursor-pointer transition-all"
                  style={{
                    background: selectedNode === node.id ? `${node.color}15` : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${selectedNode === node.id ? `${node.color}40` : 'rgba(255,255,255,0.06)'}`,
                    transform: selectedNode === node.id ? 'scale(1.02)' : 'scale(1)',
                  }}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: node.color }} />
                    <span className="font-mono text-[8px] uppercase tracking-widest" style={{ color: node.color }}>{node.context}</span>
                  </div>
                  <p className="font-mono text-[9px] font-black" style={{ color: '#d4c5a9' }}>{node.label}</p>
                  <p className="font-mono text-[8px] mt-1" style={{ color: 'rgba(212,197,169,0.35)' }}>Layer {node.layer} · {node.connections.length} links</p>
                </div>
              ))}
            </div>

            {selectedNodeData && (
              <div className="rounded-xl p-5" style={{ background: `${selectedNodeData.color}08`, border: `1px solid ${selectedNodeData.color}25` }}>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full" style={{ background: selectedNodeData.color }} />
                  <h3 className="font-mono text-sm font-black uppercase tracking-wide" style={{ color: '#F5F0E8' }}>{selectedNodeData.label}</h3>
                  <span className="font-mono text-[8px] px-2 py-0.5 rounded uppercase" style={{ background: `${selectedNodeData.color}15`, color: selectedNodeData.color }}>{selectedNodeData.context}</span>
                </div>
                <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: 'rgba(212,197,169,0.4)' }}>Connected Nodes ({connectedNodes.length})</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {connectedNodes.map(cn => (
                    <div key={cn.id} className="rounded-lg p-3" style={{ background: `${cn.color}08`, border: `1px solid ${cn.color}20` }}>
                      <div className="flex items-center gap-1.5 mb-1">
                        <div className="w-1.5 h-1.5 rounded-full" style={{ background: cn.color }} />
                        <span className="font-mono text-[8px] uppercase tracking-widest" style={{ color: cn.color }}>{cn.context}</span>
                      </div>
                      <p className="font-mono text-[9px] font-black" style={{ color: '#d4c5a9' }}>{cn.label}</p>
                      <p className="font-mono text-[8px] mt-1" style={{ color: 'rgba(212,197,169,0.35)' }}>Layer {cn.layer}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
